
import { BROADCAST_CONSTANT } from 'src/utils/constants';
import {
  CONSTANTS as STORAGE_CONSTANTS,
  clearParticularKeyStorage,
  getStorage,
  setStorage,
} from '@adapter/web-storage';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

import { UserDataState } from './types';
import { STORAGE_KEY_CONSTANTS, STORAGE_TYPE } from 'src/utils/storage/constant';
import { getLoginPageURL } from 'src/utils/helpers';

export const initialState: Omit<UserDataState, 'setIsHydrating'> = {
  userData: null,
  userAttributes: null,
  isUserDataLoading: false,
  isUserDataError: false,
  userDataError: null,
  isStoreHydrating: true,
  isMaskedDataStored: false,
  lastApiTriggerTimeStamp: null, // this will help us to know when the last time data was fetched
};

const isEncrypted = true;
const LocalStorageDataVersion = 0.1; //version of the local storage key

// Backend has 30 days so it's good that we reduce it by 10 minutes
const thirtyDaysMinusTenMinutes =
  STORAGE_CONSTANTS.THIRTY_DAYS_IN_MINS - STORAGE_CONSTANTS.TEN_MINUTES;
const isProductionEnviorment = process.env.NODE_ENV === 'production';

const customLocalStorageConfig = {
  getItem: (name: string): string | null => {
    try {
      return getStorage(STORAGE_TYPE.LOCAL_STORAGE, name, isEncrypted);
    } catch (err) {
      console.error('❌ Error reading from localStorage', err);
      return null;
    }
  },

  setItem: (name: string, value: string): void => {
    try {
      if (typeof value === 'string') {
        const parsedValue = JSON.parse(value);

        //Persisting data to local storage only if the API fetch user data successfully (i.e., user data is returned and the api request does not fail).
        const { userData, isUserDataLoading, isUserDataError } =
          parsedValue?.state;

        if (userData && !isUserDataLoading && !isUserDataError) {
          setStorage(
            STORAGE_TYPE.LOCAL_STORAGE,
            name,
            value,
            isEncrypted,
            thirtyDaysMinusTenMinutes
          );
        }
      }
    } catch (err) {
      console.error('❌ Error writing to localStorage', err);
    }
  },

  removeItem: (name: string): void => {
    try {
      clearParticularKeyStorage(STORAGE_TYPE.LOCAL_STORAGE, name);
    } catch (err) {
      console.error('❌ Error removing from localStorage', err);
    }
  },
};

//we will need to clear this store on logout to remove persisted data from local storage
//use clearUserDataStore action to clear the store
export const userDataStore = create<UserDataState>()(
  persist(
    !isProductionEnviorment
      ? devtools(
          set => ({
            ...initialState,
            setIsHydrating: (isStoreHydrating: boolean) =>
              set({ isStoreHydrating }),
          }),
          { name: 'userDataStore' }
        )
      : set => ({
          ...initialState,
          setIsHydrating: (isStoreHydrating: boolean) =>
            set({ isStoreHydrating }),
        }),
    {
      name: STORAGE_KEY_CONSTANTS.USER_DATA_PERSIST_BY_ZUSTAND, //name of local storage key which refer stored user data in local storage
      version: LocalStorageDataVersion,
      storage: createJSONStorage(() => customLocalStorageConfig),

      onRehydrateStorage: () => state => {
        state?.setIsHydrating(false);
      },

      // Optional: You can define migrations here if needed
      // check documentation for more details
      // https://zustand.docs.pmnd.rs/middlewares/persist#persisting-a-state-through-versioning-and-migrations
    }
  )
);

// BroadcastChannel for cross-tab authentication events
type AuthBroadcastMessage = { type: 'LOGOUT' };

let authBroadcastChannel: BroadcastChannel | null = null;

// Initialize BroadcastChannel for cross-tab communication
if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
  authBroadcastChannel = new BroadcastChannel('auth-sync-channel');

  // Listen for messages from other tabs
  authBroadcastChannel.addEventListener('message', event => {
    const message = event.data as AuthBroadcastMessage;

    try {
      if (message.type === BROADCAST_CONSTANT.LOGOUT) {
        // Another tab logged out - redirect to login
        window.location.href = getLoginPageURL();
      }
    } catch (error) {
      console.error('❌ Error handling auth broadcast message:', error);
    }
  });
}

// Export function to broadcast logout to all tabs
export const broadcastLogout = () => {
  if (authBroadcastChannel) {
    authBroadcastChannel.postMessage({ type: BROADCAST_CONSTANT.LOGOUT });
  }
};
